import React from 'react';
import useReveal from '../hooks/useReveal';
import SEO from '../components/SEO';
const TOC = [
  ['01', 'What Is Apache Airflow & When To Use It'],
  ['02', 'Understanding DAGs — Anatomy and Best Practices'],
  ['03', 'Tasks, Operators, and Sensor Types'],
  ['04', 'Building Your First Production Pipeline'],
  ['05', 'Scheduling, Retries & Failure Handling'],
  ['06', 'Monitoring, Logging & Alerting'],
  ['07', 'Deploying Airflow on AWS'],
];

const ARTICLES = [
  { n:'01', cat:'Backend Architecture', title:'Why Backend Architecture Matters More Than Features', desc:'Most teams focus heavily on building features quickly. Without solid architecture, systems become unmaintainable at scale.' ,url:'/blog/backend-architecture' },
  { n:'02', cat:'AI & Automation', title:'How AI is Transforming Business Workflows (Beyond Chatbots)', desc:'Real AI value: automating document processing, extracting structured data, enabling intelligent search — not demos.' ,url:'/blog/ai-workflows' },
  { n:'03', cat:'Framework Comparison', title:'Django vs FastAPI: Choosing the Right Backend Framework', desc:'Both are powerful — but serve different problems. A practical guide to when to use each, and when to use both.' ,url:'/blog/django-vs-fastapi' },
  { n:'04', cat:'API Design', title:'Building Reliable APIs: What Most Systems Get Wrong', desc:'Inconsistent formats, poor error handling, no versioning, weak auth. A guide to APIs built to last.' ,url:'/blog/building-reliable-apis' },
  { n:'05', cat:'Business Automation', title:'From Manual Work to Automation: A Real Business Shift', desc:'Integrating automation layers — without replacing systems — and what that actually looks like in practice.' ,url:'/blog/business-automation' },
];

const MEDIUM = [
  { lab:'Part 1', title:'MLflow Basic Approach — Logging', desc:'Getting started with MLflow experiment tracking — how to log parameters, metrics, and artifacts from your ML training runs.' ,url:'https://medium.com/@jain.roh/ml-flow-basic-approach-part-1-logging-e528a92922f5'},
  { lab:'Part 2', title:'MLflow Basic Approach — Deploy', desc:'Serving your logged MLflow models as REST APIs — from local testing to a production deployment workflow.', url:'https://medium.com/@jain.roh/ml-flow-basic-approach-part-2-deploy-64f759e9161b' },
  { lab:'Part 3', title:'MLflow Basic Approach — Save, Deploy and Reuse', desc:'The full lifecycle: model registry, versioning, stage promotion, and reusing trained models across projects.',url:'https://medium.com/@jain.roh/ml-flow-basic-approach-part-3-save-deploy-and-reuse-model-38a7535365d7' },
  { lab:'Deep Dive', title:'Apache Airflow: DAGs, Tasks, and Operators Explained', desc:'A clear, jargon-free breakdown of Airflow\'s core concepts — written for engineers who need to understand it fast.',url:'https://medium.com/@jain.roh/understanding-apache-airflow-dags-tasks-and-operators-explained-simply-7d004c19cf7b' },
];

export default function Resources() {
  useReveal();
  return (
    <div className="page">
      <SEO
  title="Resources — Free Developer Tools, Guides & Templates | Arhatinfo"
  description="Free resources for developers — IP tools, JSON formatter, PDF tools, text diff, development guides and templates."
  keywords="free developer tools, programming guides, Django templates, API design guide, Python resources"
  canonical="https://arhatinfo.com/resources"
  og={{ title:"Free Developer Resources — Tools & Guides | Arhatinfo", url:"https://arhatinfo.com/resources" }}
/>
      <div className="ph rv">
        <div className="s-label">Resources</div>
        <h1>Engineering Knowledge,<br />Freely Shared</h1>
        <p className="lead">E-books, technical articles, and guides from hands-on experience building backend systems, AI workflows, and cloud infrastructure at scale.</p>
      </div>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">

          {/* E-Book */}
          <div className="ebook-feat rv">
            <div>
              <span className="ebook-badge">📖 Featured E-Book</span>
              <h2>Hands-On Data Pipelines<br />with Apache Airflow</h2>
              <p>A practical guide to understanding Apache Airflow from first principles — DAGs, tasks, operators, sensors, and workflow orchestration for data engineering teams who need to ship reliable pipelines.</p>
              <p>Written from real production experience running Airflow at scale — not just the happy path from the docs.</p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a href="https://rohitjain058.gumroad.com/l/haztmt" target='_blank' className="btn-p">Read the E-Book →</a>
                <a href="https://forms.office.com/pages/responsepage.aspx?id=ECJmNjtrK0u_03Wsuq-DCcT1uiWRvzlBmpdB7e9CALdUNUUySllROElKS0Q1TlFGVVlIWEdMNVJQOS4u&embed=true&route=shorturl" className="btn-g">Download Sample Chapter</a>
              </div>
            </div>
            <div className="toc-box">
              <h4>Table of Contents</h4>
              {TOC.map(([n, l]) => (
                <div key={n} className="toc-item">
                  <span className="toc-num">{n}</span>{l}
                </div>
              ))}
            </div>
          </div>

          {/* Articles */}
          <div className="s-label rv" style={{ marginBottom: '1.5rem' }}>Engineering Articles</div>
          <h2 className="s-title rv" style={{ marginBottom: '2rem' }}>Technical Deep Dives</h2>
          <div className="art-list rv">
            {ARTICLES.map((a, i) => (
              <a key={i} href={a.url} className="art-row">
                <span className="art-num">{a.n}</span>
                <div className="art-content">
                  <div className="art-cat">{a.cat}</div>
                  <h4>{a.title}</h4>
                  <p>{a.desc}</p>
                </div>
                <span className="art-arr">→</span>
              </a>
            ))}
          </div>

          {/* Medium */}
          <div className="s-label rv" style={{ marginBottom: '1.2rem' }}>On Medium</div>
          <h2 className="s-title rv" style={{ marginBottom: '1.5rem' }}>MLflow Series</h2>
          <div className="med-grid rv">
            {MEDIUM.map((m, i) => (
              <a key={i} href={m.url} className={`med-card d${i + 1}`} target="_blank" rel="noopener noreferrer">
                <div className="med-lab">{m.lab}</div>
                <h4>{m.title}</h4>
                <p>{m.desc}</p>
                <span className="med-link">Read on Medium →</span>
              </a>
            ))}
          </div>

        </div>
      </section>
    </div>
  );
}
